import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip
} from '@mui/material';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart
} from 'recharts';
import { formatCurrency } from '../../../utils/formatNumber';

interface BalanceReportContentProps {
  report: any;
  year: number;
}

const BalanceReportContent: React.FC<BalanceReportContentProps> = ({ report, year }) => {
  // Preparar datos para el gráfico combinado
  const chartData = report.report.map((item: any) => ({
    month: item.month.substring(0, 3),
    ingresos: item.income.total,
    gastos: item.expenses.total,
    balance: item.monthly_balance,
    balanceAfterTeacher: item.balance_after_last_teacher_payment,
    cumulative: item.cumulative_balance
  }));

  // Tooltip personalizado
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Paper sx={{ p: 2, bgcolor: 'background.paper', boxShadow: 3 }}>
          <Typography variant="body2" fontWeight="bold" gutterBottom>
            {label}
          </Typography>
          <Typography variant="body2" color="success.main">
            Ingresos: {formatCurrency(data.ingresos)}
          </Typography>
          <Typography variant="body2" color="error.main">
            Gastos: {formatCurrency(data.gastos)}
          </Typography>
          <Typography variant="body2" color="primary.main" fontWeight="bold">
            Balance Mensual: {formatCurrency(data.balance)}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1, borderTop: '1px solid #eee', pt: 1 }}>
            Saldo después de pagar profesores: {formatCurrency(data.balanceAfterTeacher)}
          </Typography>
        </Paper>
      );
    }
    return null;
  };

  return (
    <Grid container spacing={3}>
      <Grid size={{ xs: 12 }}>
        <Card>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                Balance Mensual después de Pagos a Profesores - {year}
              </Typography>
              <Chip 
                label={`Promedio: ${formatCurrency(report.summary.average_balance_after_teacher_payments)}`}
                color="primary"
                size="small"
              />
            </Box>

            {/* Tarjetas de resumen */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card variant="outlined" sx={{ bgcolor: 'success.50' }}>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">
                      Total Ingresos
                    </Typography>
                    <Typography variant="h5" color="success.main">
                      {formatCurrency(report.summary.total_income)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card variant="outlined" sx={{ bgcolor: 'error.50' }}>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">
                      Total Gastos
                    </Typography>
                    <Typography variant="h5" color="error.main">
                      {formatCurrency(report.summary.total_expenses)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card variant="outlined" sx={{ bgcolor: report.summary.total_monthly_balance >= 0 ? 'info.50' : 'warning.50' }}>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">
                      Balance Neto
                    </Typography>
                    <Typography variant="h5" color={report.summary.total_monthly_balance >= 0 ? 'success.main' : 'error.main'}>
                      {formatCurrency(report.summary.total_monthly_balance)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card variant="outlined" sx={{ bgcolor: 'primary.50' }}>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">
                      Saldo Prom. después de Profesores
                    </Typography>
                    <Typography variant="h5" color="primary.main">
                      {formatCurrency(report.summary.average_balance_after_teacher_payments)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Gráfico combinado */}
            <Box sx={{ height: 400, mb: 4 }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis tickFormatter={(v) => formatCurrency(v as number)} />
                  <RechartsTooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="ingresos" fill="#4caf50" name="Ingresos" />
                  <Bar dataKey="gastos" fill="#f44336" name="Gastos" />
                  <Line 
                    type="monotone" 
                    dataKey="balanceAfterTeacher" 
                    stroke="#1976d2" 
                    name="Saldo después de profesores" 
                    strokeWidth={3}
                    dot={{ r: 4 }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="balance" 
                    stroke="#ff9800" 
                    name="Balance Mensual" 
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={{ r: 3 }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>

            {/* Tabla detallada */}
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell>Mes</TableCell>
                    <TableCell align="right">Ingresos</TableCell>
                    <TableCell align="right">Gastos Profesores</TableCell>
                    <TableCell align="right">Otros Gastos</TableCell>
                    <TableCell align="right">Balance Mensual</TableCell>
                    <TableCell align="right">Saldo después de Profesores</TableCell>
                    <TableCell align="right">Balance Acumulado</TableCell>
                    <TableCell>Último Pago Profesor</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.report.map((row: any) => (
                    <TableRow key={row.month}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {row.month}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ color: 'success.main' }}>
                        {formatCurrency(row.income.total)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: 'error.main' }}>
                        {formatCurrency(row.expenses.teacher_payments)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: 'error.main' }}>
                        {formatCurrency(row.expenses.other_expenses)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', color: row.monthly_balance >= 0 ? 'success.main' : 'error.main' }}>
                        {formatCurrency(row.monthly_balance)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                        {formatCurrency(row.balance_after_last_teacher_payment)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(row.cumulative_balance)}
                      </TableCell>
                      <TableCell>
                        {row.last_teacher_payment_date ? (
                          <Typography variant="caption" display="block">
                            {row.last_teacher_payment_date}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            Sin pagos
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default BalanceReportContent;